import { isoDateLocal, previousWeekRange } from './aggregate';
import type { DateRange } from './aggregate';

/**
 * EL PERIODO DEL TABLERO: un solo estado, compartido por Global y Tienda
 * (SPEC `2026-10-12-tableros-homologados.md`, «Periodo»).
 *
 * Antes cada vista tenía el suyo y se copiaban en UN sentido: «Hoy (en vivo)» de
 * Global era el rango `{hoy, hoy}` y al pasar a Tienda llegaba como «Periodo ·
 * 08/10–08/10», sin el marcador «ahora» y con deltas «vs anterior». Ahora el
 * vivo es un MODO, no un rango de un día, y las dos vistas leen el mismo valor.
 */
export type Periodo = { modo: 'vivo' } | { modo: 'rango'; desde: string; hasta: string };

export const PERIODO_VIVO: Periodo = { modo: 'vivo' };

/**
 * Límites FIJOS de los campos de fecha. Nunca atados al otro campo.
 *
 * ⚠️ El `max` de «Desde» atado a «Hasta» reiniciaba el búfer del año en Chrome
 * mientras se tecleaba: quien escribía 05/10/2026 terminaba con 0026-10-05, que
 * es exactamente la URL de los 500 de producción del 11-oct. Medido en el arnés
 * de la auditoría: sin min/max, o con min/max que no cambian, el año sale bien.
 */
export const FECHA_MINIMA = '2024-01-01';
export const ANIO_MINIMO = 2024;

/** Tope de días del rango (inclusivo). El mismo que valida la API. */
export const MAX_DIAS_RANGO = 366;

const FORMATO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** Hoy en `YYYY-MM-DD`, hora local del navegador (la misma que usan los atajos). */
export function hoyIso(ahora: Date = new Date()): string {
  return isoDateLocal(ahora);
}

export function periodoRango(r: DateRange): Periodo {
  return { modo: 'rango', desde: r.desde, hasta: r.hasta };
}

/** Periodo con el que abre el tablero: la semana anterior completa (lun-dom). */
export function periodoPorDefecto(ahora: Date = new Date()): Periodo {
  return periodoRango(previousWeekRange(ahora));
}

/**
 * El rango que se le pide a los endpoints que SIEMPRE exigen `desde`/`hasta`
 * (`/sucursales`, `negados-*`): el vivo es el día de hoy.
 */
export function rangoEfectivo(p: Periodo, hoy: string = hoyIso()): DateRange {
  return p.modo === 'vivo' ? { desde: hoy, hasta: hoy } : { desde: p.desde, hasta: p.hasta };
}

export function mismoPeriodo(a: Periodo, b: Periodo): boolean {
  if (a.modo === 'vivo' || b.modo === 'vivo') return a.modo === b.modo;
  return a.desde === b.desde && a.hasta === b.hasta;
}

/** Llave estable de un periodo, para dependencias de efectos (`vivo` o `desde|hasta`). */
export function clavePeriodo(p: Periodo): string {
  return p.modo === 'vivo' ? 'vivo' : `${p.desde}|${p.hasta}`;
}

/** `YYYY-MM-DD` -> `DD/MM/AAAA`, CON año. */
export function fechaLarga(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Texto del encabezado: «En vivo · hoy 13:05» o «Periodo · 05/10/2026 –
 * 11/10/2026». Con año: sin él, quien veía «05/10–11/10» no podía saber que el
 * campo había quedado en el año 0026.
 */
export function etiquetaPeriodo(p: Periodo, ahora: Date = new Date()): string {
  if (p.modo === 'vivo') return `En vivo · hoy ${hhmm(ahora)}`;
  return `Periodo · ${fechaLarga(p.desde)} – ${fechaLarga(p.hasta)}`;
}

/* ------------------------------------------------------------------ *
 * BORRADOR: lo que el usuario está tecleando, todavía sin aplicar.
 * ------------------------------------------------------------------ */

/**
 * Borrador del periodo. `vivo` marca que se eligió «Hoy (en vivo)»; mientras lo
 * esté, los campos muestran el día de hoy. Teclear en cualquiera de los dos lo
 * apaga. `desde`/`hasta` pueden venir VACÍOS: es lo que entrega un
 * `<input type="date">` mientras la fecha no está completa.
 */
export interface BorradorPeriodo {
  vivo: boolean;
  desde: string;
  hasta: string;
}

export function borradorDePeriodo(p: Periodo, hoy: string = hoyIso()): BorradorPeriodo {
  return p.modo === 'vivo'
    ? { vivo: true, desde: hoy, hasta: hoy }
    : { vivo: false, desde: p.desde, hasta: p.hasta };
}

/**
 * El periodo que resultaría de aplicar el borrador (sin validarlo).
 *
 * Un rango tecleado {hoy, hoy} se aplica como el VIVO: hay un solo modo para el
 * día en curso (mismo criterio que la app). Si no, «hoy» tecleado y «Hoy (en
 * vivo)» darían dos vistas del mismo día, una con «vs ayer» y otra con «vs
 * anterior».
 */
export function periodoDeBorrador(b: BorradorPeriodo, hoy: string = hoyIso()): Periodo {
  if (b.vivo || (b.desde === hoy && b.hasta === hoy)) return PERIODO_VIVO;
  return { modo: 'rango', desde: b.desde, hasta: b.hasta };
}

export interface OpcionesValidacion {
  /** Hoy en `YYYY-MM-DD`. Se inyecta en las pruebas. */
  hoy?: string;
  /** `true` sólo en Presupuesto: la meta del mes en curso llega hasta fin de mes. */
  permitirFuturo?: boolean;
  maxDias?: number;
}

/** Días entre dos fechas `YYYY-MM-DD`, contando las dos puntas. */
export function diasInclusivos(desde: string, hasta: string): number {
  const [y1, m1, d1] = desde.split('-').map(Number);
  const [y2, m2, d2] = hasta.split('-').map(Number);
  // UTC para que un cambio de horario no le reste una hora a la cuenta.
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000) + 1;
}

/**
 * ¿Existe la fecha en el calendario? Con aritmética, NO con `Date`: `Date.UTC`
 * convierte los años 0-99 en 19xx, y una fecha a medio teclear como
 * `0026-02-29` se validaría contra 1926.
 */
function esFechaReal(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1) return false;
  const bisiesto = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const diasDelMes = [31, bisiesto ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
  return d <= diasDelMes;
}

/** Problema de UNA fecha (formato, año, calendario), o `null` si está bien. */
function problemaDeFecha(valor: string, nombre: string, anioMax: number): string | null {
  if (!valor || !FORMATO_FECHA.test(valor)) {
    return `Completa la fecha «${nombre}» (día, mes y año).`;
  }
  const anio = Number(valor.slice(0, 4));
  if (anio < ANIO_MINIMO || anio > anioMax) {
    return `El año de «${nombre}» debe estar entre ${ANIO_MINIMO} y ${anioMax}.`;
  }
  if (!esFechaReal(valor)) {
    return `«${nombre}» no es una fecha que exista.`;
  }
  return null;
}

/**
 * Valida un rango con las MISMAS reglas que la API (`validarRangoTablero`):
 * fecha completa, año entre 2024 y el actual + 1, fecha de calendario real,
 * desde ≤ hasta, hasta ≤ hoy (salvo Presupuesto) y como mucho 366 días.
 * Devuelve el primer problema en español, o `null` si se puede aplicar.
 *
 * El front valida para no mandar basura; la API valida otra vez por si la
 * manda alguien más. Las dos tienen que decir lo mismo.
 */
export function validarRango(
  desde: string,
  hasta: string,
  opts: OpcionesValidacion = {},
): string | null {
  const hoy = opts.hoy ?? hoyIso();
  const anioMax = Number(hoy.slice(0, 4)) + 1;
  const maxDias = opts.maxDias ?? MAX_DIAS_RANGO;

  const pDesde = problemaDeFecha(desde, 'Desde', anioMax);
  if (pDesde) return pDesde;
  const pHasta = problemaDeFecha(hasta, 'Hasta', anioMax);
  if (pHasta) return pHasta;

  // `YYYY-MM-DD` ordena igual como texto que como fecha: aquí sí se puede
  // comparar con `>` porque el formato ya está validado arriba.
  if (desde > hasta) return '«Desde» no puede ser posterior a «Hasta».';
  if (!opts.permitirFuturo && hasta > hoy) {
    return `«Hasta» no puede ser posterior a hoy (${fechaLarga(hoy)}).`;
  }
  const dias = diasInclusivos(desde, hasta);
  if (dias > maxDias) {
    return `El periodo no puede pasar de ${maxDias} días (elegiste ${dias}).`;
  }
  return null;
}

/** Validación del borrador: el vivo siempre es válido. */
export function validarBorrador(b: BorradorPeriodo, opts: OpcionesValidacion = {}): string | null {
  return b.vivo ? null : validarRango(b.desde, b.hasta, opts);
}
